"use client";

import styles from "styles/components/footer.module.css";
import base from "lib/base";
import { languageBuild } from "lib/language";
import { useWebInfoStore } from "context/webInfoStore";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Sunrise,
  Youtube,
  Send,
  Globe,
} from "lucide-react";

const resolveSocialIcon = (platform) => {
  switch (String(platform || "").toLowerCase()) {
    case "facebook":
      return Facebook;
    case "instagram":
      return Instagram;
    case "twitter":
      return Twitter;
    case "linkedin":
      return Linkedin;
    case "youtube":
      return Youtube;
    case "tiktok":
      return Sunrise;
    case "telegram":
      return Send;
    case "website":
      return Globe;
    default:
      return Globe;
  }
};

const normalizeUrl = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const mailtoHref = (value) => {
  const v = String(value ?? "").trim().replace(/^mailto:/i, "");
  return v ? `mailto:${v}` : "";
};

/** Утасны дугаар — `tel:` холбоос (callto биш, стандарт) */
const telHref = (value) => {
  const v = String(value ?? "").trim();
  if (!v) return "";
  const digits = v.replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : "";
};

const getPrimaryEmail = (emails) => {
  if (!Array.isArray(emails)) return null;
  return emails.find((email) => email?.isDefault) || emails[0] || null;
};

const stripHtml = (value) => {
  if (!value) return "";
  return String(value)
    .replace(/<[^>]*>/g, "")
    .trim();
};

const clampText = (value, maxLength = 152) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const FOOTER_COPYRIGHT_YEAR_START = 2024;

const Footer = ({ info, language = "mn" }) => {
  const storeWebInfo = useWebInfoStore((state) => state.webInfo);
  const sourceInfo = info ?? storeWebInfo;
  const resolvedInfo = Array.isArray(sourceInfo)
    ? sourceInfo[0] || {}
    : sourceInfo || {};
  const core = resolvedInfo?.core || {};
  const contact = resolvedInfo?.contact || {};
  const socials = Array.isArray(resolvedInfo?.socials)
    ? resolvedInfo.socials
    : [];

  const title =
    languageBuild(resolvedInfo, "siteTitle", language) ||
    languageBuild(core, "siteTitle", language) ||
    "GERELT";
  const descriptionRaw =
    languageBuild(resolvedInfo, "aboutShortDescription", language) ||
    languageBuild(resolvedInfo, "shortDescription", language) ||
    languageBuild(resolvedInfo, "siteShortInfo", language) ||
    "Gerelt Publishing";
  const description = clampText(stripHtml(descriptionRaw), 152);

  const logoPath =
    languageBuild(resolvedInfo, "logo", language) ||
    resolvedInfo?.logo ||
    languageBuild(resolvedInfo, "whiteLogo", language) ||
    core?.logo ||
    null;
  const logoUrl = logoPath
    ? /^https?:\/\//i.test(logoPath)
      ? logoPath
      : `${base.cdnUrl}/${logoPath}`
    : null;

  const emails = Array.isArray(contact?.emails) ? contact.emails : [];
  const phones = Array.isArray(contact?.phones) ? contact.phones : [];
  const primaryEmail = getPrimaryEmail(emails);
  const secondaryEmail = emails.find((email) => email !== primaryEmail) || null;

  const addressText =
    languageBuild(resolvedInfo, "address", language) ||
    contact?.address ||
    contact?.branches?.[0]?.addressLines?.join(", ") ||
    "";

  const workingHoursText =
    languageBuild(resolvedInfo, "workingHours", language) || "";
  const workingHoursNormalized = String(workingHoursText).replace(/\\n/g, "\n");
  const workingHoursLines = workingHoursNormalized
    ? workingHoursNormalized.split(/\r?\n/).filter(Boolean)
    : [];

  const yearNow = new Date().getFullYear();
  const copyrightYears =
    yearNow <= FOOTER_COPYRIGHT_YEAR_START
      ? String(FOOTER_COPYRIGHT_YEAR_START)
      : `${FOOTER_COPYRIGHT_YEAR_START}–${yearNow}`;

  return (
    <footer className={styles.footer}>
      <div className="container">
        <div className="row">
          {/* Logo & Description */}
          <div className="col-lg-4 col-md-6">
            <div className={styles.logoArea}>
              <div className={styles.logo}>
                {logoUrl ? (
                  <img src={logoUrl} alt={title} className={styles.logoImage} />
                ) : (
                  <>
                    <Sunrise className={styles.logoIcon} />
                    <span className={styles.logoText}>{title}</span>
                  </>
                )}
              </div>
              <p className={styles.description}>{description}</p>
            </div>
          </div>

          {/* Email */}
          <div className="col-lg-3 col-md-6">
            <h4 className={styles.columnTitle}>И-мэйл хаяг</h4>
            {primaryEmail ? (
              <div className={styles.infoItem}>
                <div className={styles.infoIconWrapper}>
                  <Mail className={styles.infoIcon} />
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoLabel}>
                    {primaryEmail.label || "Ерөнхий мэдээлэл"}
                  </span>
                  {mailtoHref(primaryEmail.value) ? (
                    <a
                      href={mailtoHref(primaryEmail.value)}
                      className={styles.infoContactLink}
                    >
                      {primaryEmail.value}
                    </a>
                  ) : (
                    <span className={styles.infoValue}>{primaryEmail.value}</span>
                  )}
                </div>
              </div>
            ) : null}
            {secondaryEmail ? (
              <div className={styles.infoItem}>
                <div className={styles.infoIconWrapper}>
                  <Mail className={styles.infoIcon} />
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoLabel}>
                    {secondaryEmail.label || "Дэмжлэг тусламж"}
                  </span>
                  {mailtoHref(secondaryEmail.value) ? (
                    <a
                      href={mailtoHref(secondaryEmail.value)}
                      className={styles.infoContactLink}
                    >
                      {secondaryEmail.value}
                    </a>
                  ) : (
                    <span className={styles.infoValue}>
                      {secondaryEmail.value}
                    </span>
                  )}
                </div>
              </div>
            ) : null}
            {emails.length > 2
              ? emails.slice(2).map((email) => (
                  <div
                    key={email?.label || email?.value}
                    className={styles.infoItem}
                  >
                    <div className={styles.infoIconWrapper}>
                      <Mail className={styles.infoIcon} />
                    </div>
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>
                        {email?.label || "И-мэйл"}
                      </span>
                      {mailtoHref(email?.value) ? (
                        <a
                          href={mailtoHref(email?.value)}
                          className={styles.infoContactLink}
                        >
                          {email?.value}
                        </a>
                      ) : (
                        <span className={styles.infoValue}>{email?.value}</span>
                      )}
                    </div>
                  </div>
                ))
              : null}
          </div>

          {/* Phone & Address */}
          <div className="col-lg-3 col-md-6">
            <h4 className={styles.columnTitle}>Утас & Хаяг</h4>
            {phones.length
              ? phones.map((phone) => (
                  <div
                    key={phone?.label || phone?.value}
                    className={styles.infoItem}
                  >
                    <div className={styles.infoIconWrapper}>
                      <Phone className={styles.infoIcon} />
                    </div>
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>
                        {phone?.label || "Утас"}
                      </span>
                      {telHref(phone?.value) ? (
                        <a
                          href={telHref(phone?.value)}
                          className={styles.infoContactLink}
                        >
                          {phone?.value}
                        </a>
                      ) : (
                        <span className={styles.infoValue}>{phone?.value}</span>
                      )}
                    </div>
                  </div>
                ))
              : null}
            {addressText ? (
              <div className={styles.infoItem}>
                <div className={styles.infoIconWrapper}>
                  <MapPin className={styles.infoIcon} />
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoLabel}>Хаяг</span>
                  <span className={styles.infoValue}>{addressText}</span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Working Hours */}
          <div className="col-lg-2 col-md-6">
            <h4 className={styles.columnTitle}>Ажлын цаг</h4>
            <div className={styles.infoItem}>
              <div className={styles.infoIconWrapper}>
                <Clock className={styles.infoIcon} />
              </div>
              <div className={styles.infoContent}>
                {workingHoursLines.length ? (
                  workingHoursLines.map((line, index) => (
                    <span
                      key={`${line}-${index}`}
                      className={styles.infoValue}
                      style={index ? { marginTop: "6px" } : undefined}
                    >
                      {line}
                    </span>
                  ))
                ) : (
                  <span className={styles.infoValue}>Тодорхойгүй</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={styles.bottomBar}>
          <div className={styles.copyright}>
            © {copyrightYears} Христ итгэлийн ном, нийтлэл, подкастууд.. Бүх
            эрх хуулиар хамгаалагдсан.
          </div>
          <div className={styles.socialIcons}>
            {socials
              .filter((item) => item?.url)
              .map((item) => {
                const Icon = resolveSocialIcon(item?.platform);
                const url = normalizeUrl(item?.url);
                if (!url) return null;
                return (
                  <a
                    key={item?._id || item?.platform || url}
                    href={url}
                    className={styles.socialBtn}
                    aria-label={
                      item?.platform
                        ? `${String(item.platform).trim()} линк`
                        : "Сошиал холбоос"
                    }
                    title={
                      item?.platform
                        ? String(item.platform).trim()
                        : "Social link"
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon className={styles.socialIcon} />
                  </a>
                );
              })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
